import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Typography,
  Drawer,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Alert } from "@mui/material";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import axios from '../../api/axios';

const UPDATE_USER = "/createuser/";

export const EditTeamUser = ({ drawerOpen, setDrawerOpen, editMember, onUserUpdated, handleDeactivateProfile }) => {

  const [initialValues, setInitialValues] = useState({});
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (editMember != null) {
      console.log(editMember)
      setInitialValues({ firstName: editMember.first, lastName: editMember.last, email: editMember.email, mobile: editMember.mobile, password: "", confirmPassword: "", role: editMember.role })
      setFormValues({ userId: editMember.email, firstName: editMember.first, lastName: editMember.last, email: editMember.email, mobile: editMember.mobile, password: "", confirmPassword: "" })
      setRole(editMember.role)
    }
  }, [editMember])

  const hasChangedAnyData = () => {
    return (
      (formValues.firstName || "").trim() !== initialValues.firstName ||
      (formValues.lastName || "").trim() !== initialValues.lastName ||
      (formValues.email || "").trim() !== initialValues.email ||
      (formValues.mobile || "").trim() !== initialValues.mobile ||
      (formValues.password || "").trim() !== "" ||
      (formValues.confirmPassword || "").trim() !== "" ||
      (role || "") !== (initialValues.role || "")
    );
  };

  // Attempt to close drawer
  const handleCloseDrawer = () => {
    if (hasUnsavedChanges()) {
      setDialogOpen(true); // Show confirmation dialog
    } else {
      setDrawerOpen(false);
    }
  };

  // Detect if form has unsaved changes
  const hasUnsavedChanges = () => {
    return (
      (formValues.firstName || "").trim() !== (initialValues.firstName || "").trim() ||
      (formValues.lastName || "").trim() !== (initialValues.lastName || "").trim() ||
      (formValues.email || "").trim() !== (initialValues.email || "").trim() ||
      (formValues.mobile || "").trim() !== (initialValues.mobile || "").trim() ||
      (formValues.password || "").trim() !== (initialValues.password || "").trim() ||
      (formValues.confirmPassword || "").trim() !== (initialValues.confirmPassword || "").trim()
    );
  };

  // Function to format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined || phoneNumber === '') return '';

    const phoneStr = String(phoneNumber);
    const cleaned = phoneStr.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneStr;
  };

  // Function to remove formatting for storage
  const unformatPhoneNumber = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined || phoneNumber === '') return '';
    const phoneStr = String(phoneNumber);
    return phoneStr.replace(/\D/g, '');
  };

  // Handle mobile number input with formatting
  const handleMobileChange = (e) => {
    const input = e.target.value;
    const cleaned = unformatPhoneNumber(input);

    // Store the raw number in formValues for API calls
    setFormValues({ ...formValues, mobile: cleaned });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [showFormWarning, setShowFormWarning] = useState(false);
  const warningRef = useRef(null);
  const [errors, setErrors] = useState({}); // store error messages

  const [role, setRole] = useState('Viewer');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    let newErrors = {};

    if (!(formValues.firstName || "").trim()) {
      newErrors.firstName = "You must enter a first name.";
    }

    if (!(formValues.lastName || "").trim()) {
      newErrors.lastName = "You must enter a last name.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!(formValues.email || "").trim() || !emailRegex.test(formValues.email || "")) {
      newErrors.email = "You must enter a valid email address.";
    }

    if ((formValues.mobile || "") && !/^\d{10}$/.test(formValues.mobile || "")) {
      newErrors.mobile = "Mobile number is optional but must be 10 digits.";
    }

    if ((formValues.password || "") && !/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(formValues.password || "")) {
      newErrors.password = "Use at least 8 characters, with a letter and a number.";
    }

    if ((formValues.password || "") !== (formValues.confirmPassword || "")) {
      newErrors.confirmPassword = "Must match with new password. Please try again.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  useEffect(() => {
    if (drawerOpen) {
      setShowFormWarning(false); // clear warning when opening drawer
      setErrors({}); // also clear field errors if needed
    }
  }, [drawerOpen]);

  const [isLoading, setIsLoading] = useState(false);

  const handleSaveMember = async () => {
    if (!hasChangedAnyData()) {
      setDrawerOpen(false);
      return;
    }

    setIsLoading(true)
    const isValid = validate();

    if (!isValid) {
      setShowFormWarning(true);
      // setTimeout(() => {
      //     warningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      // }, 0);
      setIsLoading(false)
      return;
    }

    setShowFormWarning(false);

    try {
      // Build the update payload - only include changed fields
      const updatePayload = {
        updateUser: true,
        userId: formValues.userId, // Use original email as userId
      };

      // Only include fields that have changed or are not empty
      if (formValues.firstName?.trim()) updatePayload.first = formValues.firstName.trim();
      if (formValues.lastName?.trim()) updatePayload.last = formValues.lastName.trim();
      if (formValues.email?.trim()) updatePayload.email = formValues.email.trim();
      updatePayload.mobile = formValues.mobile.trim();
      if (role) updatePayload.role = role;

      // Only include password if it's been entered
      if (formValues.password?.trim()) {
        updatePayload.password = formValues.password;
        updatePayload.rePassword = formValues.confirmPassword;
      }

      // Add orgId if you have it in your context/state
      // if (orgId) updatePayload.orgId = orgId;

      console.log("Updating user with payload:", updatePayload);

      const response = await axios.post(UPDATE_USER, updatePayload, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data.code === 0) {
        console.log("User updated successfully:", response.data.user);

        // Close the drawer
        setDrawerOpen(false);

        const updatedUser = {
          ...editMember,
          first: formValues.firstName?.trim() || editMember.first,
          last: formValues.lastName?.trim() || editMember.last,
          email: formValues.email?.trim() || editMember.email,
          mobile: formValues.mobile?.trim(),
          role: role
        };

        if (onUserUpdated) {
          onUserUpdated(updatedUser, editMember.id); // pass updated user and original email
        }

      } else {
        throw new Error(response.data.message || 'Update failed');
      }

    } catch (error) {
      console.error("Error updating user:", error);

      if (error.response?.status === 409) {
        // 👇 Field-level validation for duplicate email
        setErrors((prev) => ({
          ...prev,
          email: "A member with this email already exists.",
        }));

        // Don’t show the general warning banner here
        setShowFormWarning(false);
      } else {
        let errorMessage = 'An unexpected error occurred';

        if (error.response) {
          // Server responded with error status
          const { status, data } = error.response;

          switch (status) {
            case 400:
              errorMessage = data.message || 'Invalid input data';
              break;
            case 404:
              errorMessage = 'User not found';
              break;
            default:
              errorMessage = data.message || `Server error (${status})`;
          }
        } else if (error.request) {
          errorMessage = 'Network error - please check your connection';
        } else {
          errorMessage = error.message || 'An unexpected error occurred';
        }

        setShowFormWarning(true);

        // Scroll to show the error banner
        setTimeout(() => {
          warningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      }
    } finally {
      setIsLoading(false);
    }

  };

  // Close drawer and dialog on confirm
  const handleConfirmClose = () => {
    setDialogOpen(false);
    setDrawerOpen(false);
    setFormValues(initialValues); // reset so no dialog on reopen
  };

  const handleDeactivateMember = () => {
    // call your API to deactivate
    console.log("Deactivating member", editMember);
    handleDeactivateProfile();
    // close drawer or handle UI changes
    setDrawerOpen(false);
  };

  // for the blue border hovering the element values using arrow keys
  const accessListboxRef = useRef(null);

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          // Only close if form is empty
          if (!hasChangedAnyData()) {
            setDrawerOpen(false);
          }
          // If there's data, ignore the click (do nothing)
          return;
        }

        // For ESC key or other close triggers, run your unsaved changes check
        handleCloseDrawer();
      }}
    >


      <form
        id="editTeamUserForm"
        onSubmit={(e) => { e.preventDefault(); handleSaveMember(); }}
        autoComplete="off"
      >
        <Box
          sx={{
            width: "600px",
            height: "974px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >

          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2,
              borderBottom: "1px solid #e0e0e0",
              position: "sticky",
              top: 0,
              bgcolor: "white",
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }} >
              Edit Profile
            </Typography>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 3,
              py: 2,
            }}
          >
            {showFormWarning && (
              <Alert
                ref={warningRef}
                severity="error"
                icon={<ErrorOutline />}
                sx={{ mb: 2, backgroundColor: "#fdecea", color: "#5f2120" }}
              >
                <strong>Missing required information</strong>
                <br />
                Please fill in the required missing information as noted.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar sx={{ width: 120, height: 120, bgcolor: 'grey.400' }}>
                {formValues.firstName || formValues.lastName
                  ? `${formValues.firstName?.[0] || ''}${formValues.lastName?.[0] || ''}`.toUpperCase()
                  : 'FL'}
              </Avatar>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Personal Information</Typography>
            <TextField
              variant="standard"
              fullWidth
              label="First Name"
              value={formValues.firstName}
              onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
              sx={{ mb: 2 }}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />

            <TextField
              variant="standard"
              fullWidth
              label="Last Name"
              value={formValues.lastName}
              onChange={(e) => setFormValues({ ...formValues, lastName: e.target.value })}
              sx={{ mb: 2 }}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />

            <TextField
              variant="standard"
              fullWidth
              label="Email"
              autoComplete='email'
              value={formValues.email}
              onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
              sx={{ mb: 2 }}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              variant="standard"
              fullWidth
              label="Mobile Number (Optional)"
              value={formatPhoneNumber(formValues.mobile)}
              onChange={handleMobileChange}
              sx={{ mb: 2 }}
              error={!!errors.mobile}
              helperText={errors.mobile}
            />

            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Access</Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                variant="standard"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                MenuProps={{
                  MenuListProps: {
                    ref: accessListboxRef,
                    onKeyDown: () => accessListboxRef.current?.classList.add('keyboard-nav'),
                    onMouseMove: () => accessListboxRef.current?.classList.remove('keyboard-nav'),
                  },
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: '#1976d214 !important',
                        fontWeight: 'bold',
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        backgroundColor: '#1976d229 !important',
                        fontWeight: 'bold',
                      },
                      '& .MuiMenuItem-root:hover': {
                        backgroundColor: '#0000000a !important',
                        fontWeight: 'normal',
                      },
                      '& .keyboard-nav .MuiMenuItem-root.Mui-focusVisible': {
                        outline: '2px solid #1976d2 !important',
                        outlineOffset: '-2px',
                      },
                    },
                  },
                }}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="useredit">Editor</MenuItem>
                <MenuItem value="user">Viewer</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Update Password</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>Only do this if you are updating member’s password.</Typography>
            <TextField
              fullWidth
              variant="standard"
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formValues.password}
              onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
              sx={{ mb: 2 }}
              error={!!errors.password}
              autoComplete="new-password"
              helperText={errors.password || "Use at least 8 characters, with a letter and a number"}
              slotProps={{
                formHelperText: {
                  sx: errors.password ? { color: "error.main" } : {}
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />

            <TextField
              fullWidth
              variant="standard"
              label="Re-enter password"
              type={showConfirmPassword ? "text" : "password"}
              value={formValues.confirmPassword}
              onChange={(e) => setFormValues({ ...formValues, confirmPassword: e.target.value })}
              sx={{ mb: 3 }}
              error={!!errors.confirmPassword}
              autoComplete="confirm-new-password"
              helperText={errors.confirmPassword || "Passwords must match"}
              slotProps={{
                formHelperText: {
                  sx: errors.confirmPassword ? { color: "error.main" } : {}
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
          {/* Buttons - inside your <form> */}
          {/* Sticky Footer */}
          <Box
            sx={{
              flexShrink: 0,
              p: 2,
              borderTop: "1px solid #e0e0e0",
              position: "sticky",
              bottom: 0,
              bgcolor: "white",
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              loading={isLoading}
              loadingPosition="start"
            >
              UPDATE PROFILE
            </Button>

            {editMember?.id !== localStorage.getItem("id") && (
              <Button
                type="button"
                onClick={handleDeactivateMember}
                variant="text"
                color="error"
              >
                DEACTIVATE MEMBER
              </Button>
            )}
          </Box>


          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            PaperProps={{
              sx: {
                width: 600,
                height: 168,
              },
            }}
          >
            <DialogTitle >You have unsaved changes</DialogTitle>
            <DialogContent sx={{ fontSize: "16px" }}>
              Do you want to close and discard changes?
            </DialogContent>
            <DialogActions>
              <Button variant='contained' onClick={handleConfirmClose} color="primary" sx={{ marginRight: "320px" }}>
                Yes, Close
              </Button>
              <Button onClick={() => setDialogOpen(false)} color="primary" autoFocus>
                No, Keep Editing
              </Button>
            </DialogActions>
          </Dialog>


        </Box>
      </form>

    </Drawer>
  )
}